import { useCallback } from 'react';

import { type ContextResolver, useRecommendations } from '@atlassian/app-recommendations-kit';

const SNOOZE_INTERVALS = {
	0: 7,
	1: 14,
	2: 30,
	3: 90,
};

export const useMsrSnoozeHandler = ({
	setFeatureVisited,
	getFeatureVisitedCount,
	setFeatureVisitedCount,
}: {
	getFeatureVisitedCount: () => number;
	setFeatureVisited: (days: number) => void;
	setFeatureVisitedCount: (count: number) => void;
}) => {
	const blockEligibilityUntilTomorrow = useCallback(() => {
		setFeatureVisited(1);
	}, [setFeatureVisited]);

	const snooze = useCallback(() => {
		const visitedCount = getFeatureVisitedCount();
		// We are going to loop the last nudge indefinitely
		const loopedVisitedCount = Math.min(visitedCount, Object.keys(SNOOZE_INTERVALS).length - 1);
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const snoozeInterval = SNOOZE_INTERVALS[loopedVisitedCount as keyof typeof SNOOZE_INTERVALS];
		setFeatureVisited(snoozeInterval);
		setFeatureVisitedCount(visitedCount + 1);
	}, [setFeatureVisited, setFeatureVisitedCount, getFeatureVisitedCount]);

	return { snooze, blockEligibilityUntilTomorrow };
};

const RECOMMENDATION_PRODUCT_KEYS = {
	JPD: 'cjn-jira-jpd',
	CONFLUENCE: 'cjn-jira-conf-v2',
} as const;

const MSR_CROSS_JOIN_NUDGE_RECOMMENDATIONS_USE_CASE = 'crossJoinNudgeFlags';

export function useMSRNudgeRecommendations({
	product,
	sessionId,
	cloudId,
	subProduct,
}: {
	cloudId: string;
	product: string;
	sessionId: string;
	subProduct: string;
}) {
	const contextResolver: ContextResolver = useCallback(
		() => ({
			product,
			useCase: MSR_CROSS_JOIN_NUDGE_RECOMMENDATIONS_USE_CASE,
			tenantId: cloudId,
			subproduct: subProduct,
			sessionId,
		}),
		[cloudId, product, subProduct, sessionId],
	);

	const { getRecommendations } = useRecommendations({ contextResolver });

	return useCallback(async (): Promise<string[]> => {
		try {
			const result = await getRecommendations({
				first: 2,
				rerank: [RECOMMENDATION_PRODUCT_KEYS.JPD, RECOMMENDATION_PRODUCT_KEYS.CONFLUENCE].map(
					(entityId) => ({ entityId }),
				),
				config: {
					cacheConfig: {
						ttl: 86400, // cache for 1 day in seconds
					},
				},
			});

			const { response: { data } = {} } = result;

			const recommendedEntities = (data ?? []).map((e) => e.entityId);

			return recommendedEntities;
		} catch (error: unknown) {
			throw new Error('Get MSR nudge recommendations error: ' + error);
		}
	}, [getRecommendations]);
}
